<template>
  <div class="glider-wrap" :class="{ 'glider-arrows-visible': arrows }">
    <div class="relative">
      <div
        ref="glider"
        class="glider"
        :class="{ 'glider-arrows-visible': arrows }"
      >
        <slot name="slides" />
      </div>
      <button
        v-if="arrows"
        ref="gliderPrev"
        role="button"
        aria-label="Previous"
        class="glider-prev"
      >
        <v-icon name="angle-left" :scale="0.5" />
      </button>

      <button
        v-show="arrows && !showSeeAllButton"
        ref="gliderNext"
        role="button"
        aria-label="Next"
        class="glider-next"
      >
        <v-icon name="angle-right" :scale="0.5" />
      </button>

      <router-link
        v-show="arrows && showSeeAllButton"
        role="button"
        aria-label="See all"
        class="glider-next glider-see-all"
        :to="seeAllPath"
      >
        <v-icon name="angle-right" :scale="0.5" />
        <p class="glider-see-all-label">See all</p>
      </router-link>
    </div>

    <div v-if="dots" class="dots-wrap" :class="{ active: showDots }">
      <div class="w-2/5 dots-label">
        {{ activeLabel }}
      </div>

      <div ref="dots" class="w-1/5 glider-dots" />

      <div class="w-2/5 dots-cta">
        <slot name="dots-cta" />
      </div>
    </div>

    <slot name="footer" />
  </div>
</template>

<script>
import Glider from "glider-js";

export default {
  name: "PromoSlider",
  inheritAttrs: false,
  inject: ["screens"],
  props: {
    arrows: {
      type: Boolean,
      default: true,
    },
    dots: {
      type: Boolean,
      default: true,
    },
    draggable: {
      type: Boolean,
      default: true,
    },
    responsiveProps: {
      type: Array,
      default: () => [],
    },
    seeAllPath: {
      type: String,
      default: "",
    },
    scrollLock: {
      type: Boolean,
      default: true,
    },
    seeAllButton: {
      type: Boolean,
      default: false,
    },
    slideLabels: {
      type: Array,
      default: () => [],
    },
    slidesToScroll: {
      type: [Number, String],
      default: 1,
    },
    slidesToShow: {
      type: [Number, String],
      default: 1,
    },
  },
  data() {
    return {
      activeLabel: this.slideLabels[0],
      glider: null,
      showSeeAllButton: this.seeAllButton,
    };
  },
  computed: {
    showDots() {
      // If the screen is greater than the md breakpoint then use the slidesToShow prop, if not use 1
      const slidesToShow = this.checkScreenSize("min", "md")
        ? this.slidesToShow
        : 1;

      return this.glider ? this.glider.slides.length > slidesToShow : false;
    },
  },
  mounted() {
    window.document.addEventListener("glider-loaded", this.hideFFScrollBars);
    window.document.addEventListener("glider-refresh", this.hideFFScrollBars);

    const arrows = {
      prev: this.$refs.gliderPrev,
      next: this.$refs.gliderNext,
    };

    this.glider = new Glider(this.$refs.glider, {
      slidesToShow: this.slidesToShow,
      slidesToScroll: this.slidesToScroll,
      dots: this.dots && this.$refs.dots,
      scrollLock: this.scrollLock,
      draggable: this.draggable,
      arrows,
      responsive: this.responsiveProps,
    });

    // Setup custom events
    this.glider.ele.addEventListener(
      "glider-slide-visible",
      this.onSlideVisible
    );
  },
  beforeDestroy() {
    this.glider && this.glider.destroy();
  },
  methods: {
    checkScreenSize(operator = "min", size = "md") {
      if (process.isClient) {
        const mq = window.matchMedia(
          `(${operator}-width: ${this.screens[size]}px)`
        );

        // window width is at least this.screens.md
        return mq.matches;
      }
    },
    hideFFScrollBars(e) {
      var scrollbarHeight = 17; // Currently 17, may change with updates
      if (/firefox/i.test(navigator.userAgent)) {
        // We only need to appy to desktop. Firefox for mobile uses
        // a different rendering engine (WebKit)
        if (window.innerWidth > 575) {
          e.target.parentNode.style.height =
            e.target.offsetHeight - scrollbarHeight + "px";
        }
      }
    },
    onSlideVisible(e) {
      const activeSlide = this.$get(e, "detail.slide");
      const slides = this.$get(this.glider, "slides");
      // Show See All button when last slide is reached
      if (this.seeAllButton) {
        this.showSeeAllButton = activeSlide === slides.length - 1;
      }
    },
  },
};
</script>

<style>
/* purgecss start ignore */
.glider-contain {
  width: 100%;
  margin: 0 auto;
  position: relative;
}
.glider {
  margin: 0 auto;
  position: relative;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  transform: translateZ(0);
}
.glider-track {
  transform: translateZ(0);
  width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  z-index: 1;
}
.glider.draggable {
  user-select: none;
  cursor: -webkit-grab;
  cursor: grab;
}
.glider.draggable .glider-slide img {
  user-select: none;
  pointer-events: none;
}
.glider.drag {
  cursor: -webkit-grabbing;
  cursor: grabbing;
}
.glider-slide {
  user-select: none;
  justify-content: center;
  align-content: center;
  width: 100%;
}
.glider-slide img {
  max-width: 100%;
}
.glider::-webkit-scrollbar {
  opacity: 0;
  height: 0;
}
.glider-prev,
.glider-next {
  user-select: none;
  position: absolute;
  outline: none;
  background: none;
  padding: 0;
  z-index: 2;
  font-size: 40px;
  text-decoration: none;
  left: 1.2rem;
  border: 0;
  top: 30%;
  cursor: pointer;
  color: #666;
  opacity: 1;
  line-height: 1;
}
.glider-prev:hover,
.glider-next:hover,
.glider-prev:focus,
.glider-next:focus {
  @apply text-brand;
}
.glider-next {
  right: -23px;
  left: auto;
}
.glider-next.disabled,
.glider-prev.disabled {
  @apply hidden !important;
}
.glider-slide {
  min-width: 150px;
}
.glider-hide {
  opacity: 0;
}
.glider-dots {
  user-select: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0 auto;
  padding: 0;
}
.glider-dot {
  background: none;
  border: 0;
  padding: 0;
  user-select: none;
  outline: none;
  display: block;
  cursor: pointer;
  color: #ccc;
  border-radius: 999px;
  background: #ccc;
  width: 12px;
  height: 12px;
  margin: 7px;
}
.glider-dot:hover,
.glider-dot:focus,
.glider-dot.active {
  background: #a89cc8;
}
@media (max-width: 36em) {
  .glider::-webkit-scrollbar {
    opacity: 1;
    -webkit-appearance: none;
    width: 7px;
    height: 3px;
  }
  .glider::-webkit-scrollbar-thumb {
    opacity: 1;
    border-radius: 99px;
    background-color: rgba(156, 156, 156, 0.25);
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.25);
  }
}

.glider-wrap {
  &.glider-arrows-visible {
    @screen md {
      @apply -mx-8;
    }
  }
}

.glider {
  @apply mb-12;

  &.glider-arrows-visible {
    @screen md {
      @apply mx-8;
    }
  }

  @screen md {
    @apply mb-20;
  }
}

.glider-prev,
.glider-next {
  top: 50%;
  transform: translateY(-50%);
}

.glider-see-all {
  @apply text-center;
}

.glider-see-all-label {
  @apply mb-0 text-grey-dark;
  font-size: 10px;
}

.glider-see-all:hover {
  @apply no-underline;

  .glider-see-all-label {
    @apply text-brand;
  }
}

.glider-next,
.glider-prev {
  &:focus {
    @apply outline-none text-black;
  }
}

.glider-prev {
  left: 1.2rem;

  @screen sm {
    left: 1rem;
  }

  @screen lg {
    left: 1.2rem;
  }
}

.glider-next,
.glider-see-all {
  right: 1.2rem;

  @screen sm {
    right: 1rem;
  }

  @screen lg {
    right: 1.2rem;
  }
}

.dots-wrap {
  @apply hidden;

  &.active {
    @apply flex items-center mx-8;
  }
}

.glider-dots {
  @apply flex items-center w-full h-auto;
}

.glider-dot {
  @apply rounded-full bg-grey cursor-pointer block p-0 border-0 text-grey cursor-pointer;
  width: 8px;
  height: 8px;
  margin: 2px 4px 4px;

  &:focus {
    outline: none;
  }

  &:hover {
    @apply bg-brand;
  }

  &.active {
    @apply bg-grey-darker;
  }
}

.dots-label {
  @apply font-sans text-grey-darkest text-sm leading-none;
}

.dots-cta {
  @apply text-right text-sm;
}

@-moz-document url-prefix() {
  .glider-track {
    margin-bottom: 17px;
  }
  .glider-wrap {
    overflow: hidden;
  }
}
/* purgecss end ignore */
</style>
